import { useCallback, useState } from 'react';
import { BasicUserInfo } from 'models/User';
import { DEBOUNCE_TIME } from 'data/global';
import { debounce, isEmail } from 'utils';
import api from 'api';

type PrefillUserBasicInfo = {
  email: string;
  form: { batch: any; change: any };
  fieldsToPrefill?: (keyof BasicUserInfo)[];
};

const DEFAULT_FIELDS = ['firstName', 'lastName'] as (keyof BasicUserInfo)[];

export default function useUserBasicInfo() {
  const [isFetchingUserBasicInfo, setIsFetchingUserBasicInfo] = useState(false);
  const [isUserBasicInfoSet, setIsUserBasicInfoSet] = useState(false);

  const resetState = useCallback(() => {
    setIsFetchingUserBasicInfo(false);
    setIsUserBasicInfoSet(false);
  }, []);

  const prefillUserBasicInfo = useCallback(
    async ({
      email,
      form: { batch, change },
      fieldsToPrefill = DEFAULT_FIELDS,
    }: PrefillUserBasicInfo) => {
      if (!isEmail(email)) {
        setIsUserBasicInfoSet(false);
        return;
      }

      try {
        setIsFetchingUserBasicInfo(true);

        const { data } = await api.users.getUserBasicInfo(email);

        const userInfo = data?.user || ({} as BasicUserInfo);
        const hasUserInfo = Object.keys(userInfo).length > 0;

        if (!hasUserInfo) {
          setIsUserBasicInfoSet(false);

          return;
        }

        if (hasUserInfo) {
          batch(() =>
            fieldsToPrefill.forEach((field) => change(field, userInfo[field])),
          );

          setIsUserBasicInfoSet(true);
        }
      } catch {
        setIsUserBasicInfoSet(false);
      } finally {
        setIsFetchingUserBasicInfo(false);
      }
    },
    [],
  );

  const onEmailChange: (args: PrefillUserBasicInfo) => void = debounce(
    async (args: PrefillUserBasicInfo) => {
      await prefillUserBasicInfo(args);
    },
    DEBOUNCE_TIME,
  );

  return {
    isFetchingUserBasicInfo,
    isUserBasicInfoSet,
    prefillUserBasicInfo,
    resetState,
    onEmailChange,
  };
}
